function normalizeTargetingValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).trim();
}

function normalizeTargetingValues(value) {
  const normalized = normalizeTargetingValue(value);
  if (Array.isArray(normalized)) {
    return normalized;
  }
  return normalized ? [normalized] : [];
}

function enableLazyLoad(googletag, pubads, lazyLoadConfig) {
  if (googletag && typeof googletag.setConfig === 'function') {
    googletag.setConfig({ lazyLoad: lazyLoadConfig });
    return;
  }
  if (pubads && typeof pubads.enableLazyLoad === 'function') {
    pubads.enableLazyLoad(lazyLoadConfig);
  }
}

function configurePageSettings(googletag, pubads, options = {}) {
  const pageConfig = {};

  if (options.disableInitialLoad === true) {
    pageConfig.disableInitialLoad = true;
  }
  if (options.singleRequest === true) {
    pageConfig.singleRequest = true;
  }
  if (options.collapseDiv) {
    pageConfig.collapseDiv = options.collapseDiv;
  }

  if (Object.keys(pageConfig).length === 0) {
    return;
  }

  if (googletag && typeof googletag.setConfig === 'function') {
    googletag.setConfig(pageConfig);
    return;
  }

  if (!pubads) return;

  if (pageConfig.disableInitialLoad && typeof pubads.disableInitialLoad === 'function') {
    pubads.disableInitialLoad();
  }
  if (pageConfig.singleRequest && typeof pubads.enableSingleRequest === 'function') {
    pubads.enableSingleRequest();
  }
  if (pageConfig.collapseDiv && typeof pubads.collapseEmptyDivs === 'function') {
    pubads.collapseEmptyDivs(pageConfig.collapseDiv === 'BEFORE_FETCH');
  }
}

/**
 * Opts one slot out of the page-level `collapseDiv`, so the page decides when
 * an empty slot's space is released.
 */
function disableSlotCollapse(slot) {
  if (!slot) return slot;
  if (typeof slot.setConfig === 'function') {
    slot.setConfig({ collapseDiv: 'DISABLED' });
  } else if (typeof slot.setCollapseEmptyDiv === 'function') {
    slot.setCollapseEmptyDiv(false);
  }
  return slot;
}

function applySlotTargeting(slot, targetingMap = {}) {
  if (!slot || !targetingMap || typeof targetingMap !== 'object') {
    return slot;
  }

  const targeting = {};
  Object.entries(targetingMap).forEach(([key, value]) => {
    const normalizedKey = String(key || '').trim();
    const normalizedValue = normalizeTargetingValue(value);
    if (!normalizedKey || !normalizedValue || normalizedValue.length === 0) {
      return;
    }
    targeting[normalizedKey] = normalizedValue;
  });

  if (Object.keys(targeting).length === 0) {
    return slot;
  }

  if (typeof slot.setConfig === 'function') {
    slot.setConfig({ targeting });
  }

  return slot;
}

function getSlotTargeting(slot, key) {
  if (!slot || !key) {
    return [];
  }

  if (typeof slot.getConfig === 'function') {
    const config = slot.getConfig('targeting') || {};
    const targeting =
      config && typeof config === 'object' && config.targeting && typeof config.targeting === 'object'
        ? config.targeting
        : config;
    return normalizeTargetingValues(targeting[key]);
  }

  return [];
}

const PREBID_MANAGED_TARGETING_KEYS = Object.freeze([
  'hb_bidder',
  'hb_adid',
  'hb_pb',
  'hb_size',
  'hb_source',
  'hb_format',
  'hb_ver'
]);

function clearSlotTargeting(slot, keys = PREBID_MANAGED_TARGETING_KEYS) {
  if (!slot || !Array.isArray(keys)) {
    return slot;
  }

  const normalizedKeys = keys
    .map((key) => String(key || '').trim())
    .filter(Boolean);

  if (normalizedKeys.length === 0) {
    return slot;
  }

  if (typeof slot.setConfig === 'function') {
    const targeting = {};
    normalizedKeys.forEach((key) => {
      targeting[key] = null;
    });
    slot.setConfig({ targeting });
  }

  return slot;
}

function applyPrebidTargetingToSlots(pbjs, slots) {
  if (!pbjs || typeof pbjs.getAdserverTargetingForAdUnitCode !== 'function') {
    return;
  }

  const slotList = Array.isArray(slots) ? slots : [slots];
  slotList.forEach((slot) => {
    if (!slot || typeof slot.getSlotElementId !== 'function') {
      return;
    }
    const slotId = slot.getSlotElementId();
    if (!slotId) return;
    const targeting = pbjs.getAdserverTargetingForAdUnitCode(slotId) || {};
    applySlotTargeting(slot, targeting);
  });
}

module.exports = {
  PREBID_MANAGED_TARGETING_KEYS,
  applyPrebidTargetingToSlots,
  applySlotTargeting,
  clearSlotTargeting,
  configurePageSettings,
  disableSlotCollapse,
  enableLazyLoad,
  getSlotTargeting
};
